import i18n from "../i18n";

/**
 * An error that remembers which status produced it.
 *
 * Without the status a caller cannot tell "your session is over" from "the
 * server is busy", and the safe-looking reaction to both is to sign the person
 * out. That is how a rate limit turned into people being thrown out of the
 * app.
 */
export class HttpError extends Error {
  readonly status: number;
  /** Seconds to wait, when the server said so. */
  readonly retryAfter: number | null;
  /** The server's machine-readable reason, when it sent one. */
  readonly code: string | null;

  constructor(status: number, message: string, retryAfter: number | null = null, code: string | null = null) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.retryAfter = retryAfter;
    this.code = code;
  }

  /** The session really is gone, as opposed to the request merely failing. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

const retryAfterSeconds = (response: Response): number | null => {
  const kopf = response.headers.get("Retry-After");
  if (!kopf) return null;
  const sekunden = Number(kopf);
  return Number.isFinite(sekunden) && sekunden > 0 ? sekunden : null;
};

/**
 * Turns a failed response into an error a person can read.
 *
 * The server does not answer with one shape. Most endpoints send `message`,
 * the rate limiter and the OAuth endpoints send `error_description`. Reading
 * only the first is why a throttled request used to surface as an alert with
 * no text at all: the field existed, it was simply called something else.
 */
export const errorFromResponse = async (response: Response): Promise<HttpError> => {
  const warten = retryAfterSeconds(response);

  let text = "";
  let code: string | null = null;
  try {
    const koerper = await response.json();
    text = koerper?.message || koerper?.error_description || koerper?.error || "";
    code = typeof koerper?.code === "string" ? koerper.code : null;
  } catch {
    // No body, or not JSON. The status still says something useful.
  }

  if (response.status === 429) {
    // Preferred over whatever the server wrote, because this one names the
    // waiting time and is in the reader's language.
    const minuten = Math.max(1, Math.ceil((warten ?? 900) / 60));
    text = String(i18n.t("errors.rate_limited", { minutes: minuten }));
  } else if (!text) {
    text = String(i18n.t("errors.generic"));
  }

  return new HttpError(response.status, text, warten, code);
};

/**
 * A text for anything that was thrown, so an alert is never empty.
 *
 * The reset page passed `error.message` straight into the alert. For a rate
 * limit that field did not exist, so people got an error box with nothing in
 * it and no idea what to do next. Anything that is not one of our own errors
 * gets the general text rather than a stack-flavoured one.
 */
export const readableMessage = (error: unknown): string => {
  if (error instanceof HttpError) return error.message;
  return String(i18n.t("errors.generic"));
};
